/**
 * AdminFinancialAccess — the custodians' screen.
 *
 * Who has asked to see confidential financial information, what was granted,
 * for how long, and on what grounds. Not a platform-administration screen: it
 * refuses anyone who is not an appointed Financial Information Custodian.
 */
import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShieldCheck, Loader2, Ban } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';
import EmergencyCountersignPanel from '@/components/finance/EmergencyCountersignPanel';
import TrusteesReport from '@/components/finance/TrusteesReport';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const d = body.detail;
    throw new Error(typeof d === 'string' ? d : (d?.reason || `Request failed (${res.status})`));
  }
  return body;
};

const when = (v) => (v ? new Date(v).toLocaleString() : '—');

export default function AdminFinancialAccess() {
  const qc = useQueryClient();
  const [shorten, setShorten] = useState({});

  const requests = useQuery({
    queryKey: ['sensitive-requests'],
    queryFn: () => api('/finance/sensitive/requests'),
    retry: false,
  });
  const grants = useQuery({
    queryKey: ['sensitive-grants'],
    queryFn: () => api('/finance/sensitive/grants'),
    retry: false,
    enabled: requests.isSuccess,
  });
  const audit = useQuery({
    queryKey: ['sensitive-audit'],
    queryFn: () => api('/finance/sensitive/audit?limit=100'),
    retry: false,
    enabled: requests.isSuccess,
  });
  const integrity = useQuery({
    queryKey: ['sensitive-audit-verify'],
    queryFn: () => api('/finance/sensitive/audit/verify'),
    retry: false,
    enabled: requests.isSuccess,
  });
  const patterns = useQuery({
    queryKey: ['sensitive-patterns'],
    queryFn: () => api('/finance/sensitive/patterns?days=30'),
    retry: false,
    enabled: requests.isSuccess,
  });

  const [hours, setHours] = useState({ timezone: 'UTC', working_from_hour: 6, working_to_hour: 22 });
  useEffect(() => {
    const w = patterns.data?.working_day;
    if (w) setHours({ timezone: w.timezone, working_from_hour: w.from_hour, working_to_hour: w.to_hour });
  }, [patterns.data]);

  const saveHours = async () => {
    const from = Number(hours.working_from_hour);
    const to = Number(hours.working_to_hour);
    if (!hours.timezone.trim()) { toast.error('Which time zone is your working day in?'); return; }
    if (!Number.isInteger(from) || !Number.isInteger(to) || from < 0 || from > 23 || to < 0 || to > 23) {
      toast.error('Working hours are whole hours of the clock, 0 to 23'); return;
    }
    if (from === to) { toast.error('A working day cannot start and end at the same hour'); return; }
    try {
      await api('/finance/sensitive/hours', {
        method: 'PUT',
        body: JSON.stringify({
          timezone: hours.timezone.trim(), working_from_hour: from, working_to_hour: to,
          weekend_days: patterns.data?.working_day?.weekend_days || [5, 6],
        }),
      });
      toast.success('Working day saved — out-of-hours access is counted against it');
      qc.invalidateQueries({ queryKey: ['sensitive-patterns'] });
    } catch (e) { toast.error(e.message); }
  };

  const decide = async (id, action) => {
    try {
      const res = await api(`/finance/sensitive/requests/${id}/decide`, {
        method: 'POST',
        body: JSON.stringify({ action, approved_duration: shorten[id] || '' }),
      });
      toast.success(res.status === 'pending_approval'
        ? `Recorded — ${res.approvals} of ${res.approvals_required} approvals`
        : `Request ${res.status}`);
      qc.invalidateQueries({ queryKey: ['sensitive-requests'] });
      qc.invalidateQueries({ queryKey: ['sensitive-grants'] });
    } catch (e) { toast.error(e.message); }
  };

  const revoke = async (id) => {
    const reason = window.prompt('Why is this access being withdrawn?');
    if (!reason) return;
    try {
      await api(`/finance/sensitive/grants/${id}/revoke`, {
        method: 'POST', body: JSON.stringify({ reason }),
      });
      toast.success('Access revoked');
      qc.invalidateQueries({ queryKey: ['sensitive-grants'] });
    } catch (e) { toast.error(e.message); }
  };

  if (requests.isError) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="custodian-denied">
        <EmergencyCountersignPanel />
        <Card className="border-amber-500/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-600">
              <Ban className="h-5 w-5" /> Not available to you
            </CardTitle>
            <CardDescription>
              Only an appointed Financial Information Custodian may review requests
              to see confidential financial information. Being a platform or
              organisation administrator does not grant this.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const pending = (requests.data?.requests || []).filter((r) => r.status === 'pending_approval');
  const decided = (requests.data?.requests || []).filter((r) => r.status !== 'pending_approval');

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="financial-access-page">
      <h1 className="text-2xl font-semibold flex items-center gap-2 mb-1">
        <ShieldCheck className="h-6 w-6 text-amber-600" /> Confidential financial access
      </h1>
      <p className="text-sm text-slate-500 mb-6">
        Requests to view restricted financial information, the grants in force, and the trail behind them.
      </p>

      <EmergencyCountersignPanel />

      {integrity.data && (
        <div className={`mb-6 rounded-lg border px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-3 ${
          integrity.data.ok ? 'border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/30'
                            : 'border-rose-500/50 bg-rose-50 dark:bg-rose-950/30'}`}
             data-testid="audit-integrity-banner">
          <div className="min-w-0">
            <p className={integrity.data.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}>
              {integrity.data.ok
                ? `Nothing in the security trail has been altered — ${integrity.data.checked} records verified.`
                : `A record has been altered or removed (number ${integrity.data.broken_at}). ${integrity.data.reason}`}
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 break-all">
              Head: <code data-testid="audit-head-hash">{(integrity.data.head?.hash || '').slice(0, 32)}…</code>
              {' · '}{integrity.data.keyed ? 'sealed with the application key' : 'unsealed'}
            </p>
            {integrity.data.note && (
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">{integrity.data.note}</p>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={() => integrity.refetch()}
                  data-testid="verify-audit-btn">Re-check</Button>
        </div>
      )}

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pending {pending.length > 0 && <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-[10px] text-white">{pending.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="grants" data-testid="tab-grants">Grants</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          <TabsTrigger value="patterns" data-testid="tab-patterns">Patterns</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-report">Trustees' report</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit trail</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-3 mt-4">
          {requests.isLoading && <Loader2 className="h-5 w-5 animate-spin" />}
          {!requests.isLoading && pending.length === 0 && (
            <p className="text-sm text-slate-500 py-8 text-center" data-testid="no-pending">
              Nothing is waiting for a decision.
            </p>
          )}
          {pending.map((r) => (
            <Card key={r.id} data-testid={`request-row-${r.id}`}>
              <CardContent className="pt-5 flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-medium">{r.resource_label}</p>
                  <p className="text-xs text-slate-500">
                    {r.user_email || r.user_id} · asked for {r.requested_duration} · {when(r.created_at)}
                    {r.is_renewal ? ' · renewal' : ''}
                  </p>
                  <p className="text-sm mt-2">{r.reason}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {r.approvals} of {r.approvals_required} approvals recorded
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Select value={shorten[r.id] || r.requested_duration}
                          onValueChange={(v) => setShorten({ ...shorten, [r.id]: v })}>
                    <SelectTrigger className="w-36" data-testid={`shorten-select-${r.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['one_time', '1h', '3h', '12h', '1d', '1w', '1m'].map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" disabled={!r.can_decide}
                          onClick={() => decide(r.id, 'approve')}
                          data-testid={`approve-btn-${r.id}`}>Approve</Button>
                  <Button size="sm" variant="outline" disabled={!r.can_decide}
                          onClick={() => decide(r.id, 'reject')}
                          data-testid={`reject-btn-${r.id}`}>Reject</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="grants" className="space-y-2 mt-4">
          {(grants.data?.grants || []).map((g) => (
            <div key={g.id} className="flex items-center justify-between gap-3 border rounded-lg px-3 py-2"
                 data-testid={`grant-row-${g.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{g.resource_label}</p>
                <p className="text-xs text-slate-500">
                  {g.user_email || g.user_id} · {g.duration} · expires {when(g.expires_at)} · viewed {g.view_count || 0}×
                </p>
              </div>
              <div className="flex items-center gap-2">
                {g.emergency && (
                  <Badge className="bg-rose-600 hover:bg-rose-600">emergency</Badge>
                )}
                <Badge variant="outline">{g.state}</Badge>
                {g.state === 'active' && (
                  <Button size="sm" variant="ghost" className="text-rose-600"
                          onClick={() => revoke(g.id)} data-testid={`revoke-btn-${g.id}`}>
                    Revoke
                  </Button>
                )}
              </div>
            </div>
          ))}
          {(grants.data?.grants || []).length === 0 && (
            <p className="text-sm text-slate-500 py-8 text-center">No grants yet.</p>
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-2 mt-4">
          {decided.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 border rounded-lg px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm truncate">{r.resource_label} · {r.user_email || r.user_id}</p>
                <p className="text-xs text-slate-500">
                  asked {r.requested_duration}, granted {r.approved_duration || '—'} · {when(r.decided_at)}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.emergency && (
                  <Badge className="bg-rose-600 hover:bg-rose-600">emergency</Badge>
                )}
                <Badge variant="outline">{r.status}</Badge>
              </div>
            </div>
          ))}
          {decided.length === 0 && (
            <p className="text-sm text-slate-500 py-8 text-center">Nothing decided yet.</p>
          )}
        </TabsContent>

        <TabsContent value="patterns" className="mt-4 space-y-4" data-testid="patterns-tab">
          {patterns.isLoading && <Loader2 className="h-5 w-5 animate-spin" />}
          {patterns.data && (
            <>
              <div className="rounded-lg border px-4 py-3 flex flex-wrap items-end gap-3"
                   data-testid="working-hours-form">
                <div>
                  <p className="text-sm font-medium">Your working day</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    What counts as "out of hours" here, rather than a fixed London night.
                  </p>
                </div>
                <div className="flex items-end gap-2 ml-auto">
                  <div>
                    <label className="text-xs text-slate-600 dark:text-slate-400">Time zone</label>
                    <input value={hours.timezone}
                           onChange={(e) => setHours({ ...hours, timezone: e.target.value })}
                           className="mt-1 h-9 w-48 rounded-md border px-2 text-sm bg-transparent"
                           placeholder="Europe/London"
                           data-testid="hours-timezone-input" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 dark:text-slate-400">From</label>
                    <input type="number" min="0" max="23" value={hours.working_from_hour}
                           onChange={(e) => setHours({ ...hours, working_from_hour: Number(e.target.value) })}
                           className="mt-1 h-9 w-20 rounded-md border px-2 text-sm bg-transparent"
                           data-testid="hours-from-input" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 dark:text-slate-400">To</label>
                    <input type="number" min="0" max="23" value={hours.working_to_hour}
                           onChange={(e) => setHours({ ...hours, working_to_hour: Number(e.target.value) })}
                           className="mt-1 h-9 w-20 rounded-md border px-2 text-sm bg-transparent"
                           data-testid="hours-to-input" />
                  </div>
                  <Button size="sm" onClick={saveHours} data-testid="hours-save-btn">Save</Button>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {[
                  ['Viewings', patterns.data.totals.views],
                  ['Refusals', patterns.data.totals.refusals],
                  ['Out of hours', patterns.data.totals.out_of_hours_views],
                  ['At weekends', patterns.data.totals.weekend_views],
                  ['Emergencies', patterns.data.totals.emergencies],
                  ['Live grants', patterns.data.totals.active_grants],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg border px-3 py-2">
                    <p className="text-xs text-slate-600 dark:text-slate-400">{label}</p>
                    <p className="text-xl font-semibold">{value}</p>
                  </div>
                ))}
              </div>

              {patterns.data.concerns.length > 0 && (
                <div className="rounded-lg border border-amber-500/40 bg-amber-50 dark:bg-slate-900 px-4 py-3"
                     data-testid="patterns-concerns">
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-1">Worth a look</p>
                  <ul className="text-sm list-disc pl-5 space-y-1">
                    {patterns.data.concerns.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                </div>
              )}

              <div className="space-y-1">
                {patterns.data.people.map((p) => (
                  <div key={p.user_id} className="flex flex-wrap items-center justify-between gap-2 border-b py-2 text-sm"
                       data-testid={`pattern-row-${p.user_id}`}>
                    <span className="truncate">{p.user_email || p.user_id}</span>
                    <span className="text-xs text-slate-600 dark:text-slate-400">
                      {p.attempts} attempts · {p.requests} requests · {p.renewals} renewals ·{' '}
                      <span className={p.refusals > 2 ? 'text-rose-600 font-medium' : ''}>{p.refusals} refused</span> ·{' '}
                      {p.views} viewings{p.out_of_hours_views > 0 && ` (${p.out_of_hours_views} out of hours)`}
                      {p.emergencies > 0 && ` · ${p.emergencies} emergency`}
                    </span>
                  </div>
                ))}
                {patterns.data.people.length === 0 && (
                  <p className="text-sm text-slate-500 py-8 text-center">
                    Nobody has touched confidential financial information in the last 30 days.
                  </p>
                )}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="report" className="mt-4">
          <TrusteesReport />
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <div className="space-y-1">
            {(audit.data?.events || []).map((e) => (
              <div key={e.id} className="text-xs border-b py-1.5 flex justify-between gap-3">
                <span className="truncate">
                  {(e.details || {}).outcome} · {(e.user || {}).email || (e.user || {}).id}
                </span>
                <span className="text-slate-400 shrink-0">{when(e.timestamp)}</span>
              </div>
            ))}
            {(audit.data?.events || []).length === 0 && (
              <p className="text-sm text-slate-500 py-8 text-center">No events recorded yet.</p>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
